<script setup lang="ts">
import {
  computed,
  onMounted,
  onScopeDispose,
  ref,
  shallowRef,
  watch,
} from "vue";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import { useAppDataService } from "../../../cbi-generator/frontend/composables/useAppDataFrontendService";
import { PurchasableState } from "../../../cbi-generator/generated/dto/PurchasableState";
import { useUtility } from "@/composables/useUtility";
import PersonPreviewDialog from "./PersonPreviewDialog.vue";
import type { PersonStaffSearchParams } from "../../../cbi-generator/generated/dto/PersonStaffSearchParams";
import type { PersonStaffSearchResult } from "../../../cbi-generator/generated/dto/PersonStaffSearchResult";
import { PersonStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/PersonStaffSearchParamsMeta";
import { SortOrder } from "../../../cbi-generator/generated/dto/SortOrder";
import { emptySearchParams } from "@/composables/useTableHelper";
import {
  fieldKind,
  readField,
  resolveFields,
  writeField,
} from "@/composables/generated/fields";
import { errorMessage } from "@/composables/generated/contracts";
import GeneratedFieldControl from "@/components/generated/GeneratedFieldControl.vue";
import {
  personLinkRenderer,
  rendererHeaders,
} from "@/components/shared/table/personLinkRenderer";

const props = withDefaults(defineProps<{ picker?: boolean }>(), {
  picker: false,
});
const emit = defineEmits<{
  personSelected: [person: PersonStaffSearchResult];
  criteriaChanged: [criteria: PersonStaffSearchParams];
}>();
const service = usePersonService();
const appData = useAppDataService();
const { formatPhoneNumber } = useUtility();
const personTypes = ref(new Map<number, string>());
const lookupError = ref("");
async function loadPersonTypes() {
  try {
    const types = await appData.getAllPersonTypeStaffs();
    if (!disposed)
      personTypes.value = new Map(
        types.map((type) => [type.typeId, type.typeName ?? "--"]),
      );
  } catch (cause) {
    if (!disposed)
      lookupError.value = `Person types could not be loaded: ${errorMessage(cause)}`;
  }
}
function emptyParams(): PersonStaffSearchParams {
  return {
    ...emptySearchParams<PersonStaffSearchParams>(PersonStaffSearchParamsMeta),
    sort: [
      { fieldName: "updatedOn", order: SortOrder.DESC },
      { fieldName: "personId", order: SortOrder.ASC },
    ],
  };
}
const draft = ref(emptyParams());
watch(draft, (value) => emit("criteriaChanged", { ...value }), {
  deep: true,
  immediate: true,
});
let applied = emptyParams();
type TableSort = { key: string; order: "asc" | "desc" };
const sortBy = ref<TableSort[]>([{ key: "updatedOn", order: "desc" }]);
function sortParams() {
  const selected = sortBy.value.map((sort) => ({
    fieldName: sort.key,
    order: sort.order === "desc" ? SortOrder.DESC : SortOrder.ASC,
  }));
  if (!selected.some((sort) => sort.fieldName === "personId"))
    selected.push({ fieldName: "personId", order: SortOrder.ASC });
  return selected;
}
const coreGroups = computed(() =>
  props.picker
    ? [
        {
          key: "quick",
          fields: [
            { key: "nameLike", label: "Name" },
            { key: "phoneLike", label: "Phone" },
            { key: "emailLike", label: "Email" },
            { key: "cardNumberLike", label: "Card number" },
          ],
        },
      ]
    : fullGroups,
);
const fullGroups = [
  {
    key: "name",
    fields: [
      { key: "firstLike", label: "First name" },
      { key: "lastLike", label: "Last name" },
      { key: "preferredLike", label: "Preferred name" },
      { key: "organizationLike", label: "Organization" },
    ],
  },
  {
    key: "address",
    fields: [
      { key: "addressLineLike", label: "Street address" },
      { key: "addressCityLike", label: "City" },
      { key: "addressStateExact", label: "State" },
      { key: "addressZipLike", label: "Zip" },
    ],
  },
  {
    key: "contact",
    fields: [
      { key: "phoneLike", label: "Phone" },
      { key: "emailLike", label: "Email" },
      { key: "cardNumberLike", label: "Card number" },
    ],
  },
];
// The full search uses individual name/address fields; pickers keep the compact tokenized name.
const hiddenFields = new Set(["nameLike", "addressLike"]);
const coreKeys = computed(
  () =>
    new Set<string>(
      coreGroups.value.flatMap((group) =>
        group.fields.map((field) => field.key),
      ),
    ),
);
const controls = computed(() =>
  resolveFields(PersonStaffSearchParamsMeta, {}, draft.value),
);
const coreControls = computed(() =>
  coreGroups.value.map((group) => ({
    key: group.key,
    fields: group.fields.flatMap((field) =>
      controls.value
        .filter((control) => control.key === field.key)
        .map((control) => ({ ...control, label: field.label })),
    ),
  })),
);
const additionalControls = computed(() =>
  controls.value.filter(
    (control) =>
      !coreKeys.value.has(control.key) &&
      !hiddenFields.has(control.key) &&
      fieldKind(control.meta) !== "custom" &&
      !(
        control.meta.isArray &&
        ["date", "datetime"].includes(fieldKind(control.meta))
      ),
  ),
);
const items = shallowRef<PersonStaffSearchResult[]>([]);
const page = ref(1);
const perPage = ref(25);
const totalCount = ref(0);
const totalPages = ref(0);
const loading = ref(false);
const error = ref("");
let requestVersion = 0;
let disposed = false;
onScopeDispose(() => {
  disposed = true;
  ++requestVersion;
});
const canLoadMore = computed(() => page.value < totalPages.value);
const displayItems = computed(() => {
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  return items.value.map((person) => {
    const phone =
      person.primaryPhoneNumber ??
      [...(person.phones ?? [])].sort((a, b) => b.assignId - a.assignId)[0]
        ?.phoneId.phoneNumber;
    const latestCard = [...(person.cards ?? [])]
      .filter(
        (card) =>
          card.active === true &&
          card.voidCloseId == null &&
          card.purchasableState === PurchasableState.ACTIVE,
      )
      .sort(
        (a, b) =>
          (b.issueDate?.getTime() ?? 0) - (a.issueDate?.getTime() ?? 0) ||
          b.assignId - a.assignId,
      )[0]?.cardNum;
    const memberships = [...(person.memberships ?? [])]
      .filter(
        (membership) =>
          membership.voidCloseId == null &&
          membership.purchasableState === PurchasableState.ACTIVE &&
          (membership.startDate == null ||
            membership.startDate.getTime() <= today) &&
          (membership.expirationDate == null ||
            membership.expirationDate.getTime() > today),
      )
      .sort(
        (a, b) =>
          (b.startDate?.getTime() ?? 0) - (a.startDate?.getTime() ?? 0) ||
          b.assignId - a.assignId,
      );
    return {
      ...person,
      // The spread row is a plain object; emit the original search row (a Read DTO).
      source: person,
      phone: phone ? formatPhoneNumber(phone) : "--",
      latestCard: latestCard ?? "--",
      type: person.personType
        ? (personTypes.value.get(person.personType.typeId) ?? "--")
        : "--",
      membershipStatus: memberships.length ? "Active" : "Inactive",
      latestMembership: memberships[0]?.membershipTypeId?.membershipTypeName,
    };
  });
});

async function load(targetPage: number, append = false) {
  const version = ++requestVersion;
  loading.value = true;
  error.value = "";
  try {
    const result = await service.searchPersonStaff(
      applied,
      targetPage,
      perPage.value,
    );
    if (disposed || version !== requestVersion) return;
    const rows = append ? [...items.value, ...result.items] : result.items;
    items.value = [
      ...new Map(rows.map((person) => [person.personId, person])).values(),
    ];
    page.value = result.page;
    totalCount.value = result.totalCount;
    totalPages.value = result.totalPages;
  } catch (cause) {
    if (!disposed && version === requestVersion)
      error.value = errorMessage(cause);
  } finally {
    if (!disposed && version === requestVersion) loading.value = false;
  }
}
function search() {
  applied = { ...draft.value, sort: sortParams() };
  for (const [key, value] of Object.entries(applied)) {
    if (typeof value === "string")
      writeField(applied, key, value.trim() || null);
  }
  items.value = [];
  page.value = 1;
  totalCount.value = 0;
  totalPages.value = 0;
  void load(1);
}
function clear() {
  draft.value = emptyParams();
  search();
}
function loadMore() {
  if (!loading.value && canLoadMore.value) void load(page.value + 1, true);
}
function resize(value: number) {
  if (value === perPage.value) return;
  perPage.value = value;
  void load(1);
}
function changePage(value: number) {
  if (value !== page.value) void load(value);
}
function changeSort(value: TableSort[]) {
  if (JSON.stringify(value) === JSON.stringify(sortBy.value)) return;
  sortBy.value = value;
  applied = { ...applied, sort: sortParams() };
  page.value = 1;
  void load(1);
}
const personNameLink = personLinkRenderer<{ personId: number }>(
  (row) => row.personId,
);
const headers = computed(() => [
  // The picker selects on name click instead, so only the results table links to the person.
  {
    title: "Name",
    key: "name",
    sortable: !props.picker,
    renderer: props.picker ? undefined : personNameLink,
  },
  { title: "ID", key: "personId", sortable: !props.picker },
  { title: "Phone", key: "phone", sortable: false },
  { title: "Email", key: "email", sortable: !props.picker },
  { title: "Date of birth", key: "dob", sortable: !props.picker },
  ...(!props.picker
    ? [
        { title: "Latest Card", key: "latestCard", sortable: false },
        { title: "Type", key: "type", sortable: false },
        { title: "Status", key: "membershipStatus", sortable: false },
        { title: "Updated", key: "updatedOn", sortable: true },
        { title: "Actions", key: "actions", sortable: false },
      ]
    : []),
]);
const nameSlot = "item.name";
const customCells = computed(() => rendererHeaders(headers.value));
const dobSlot = "item.dob";
const updatedSlot = "item.updatedOn";
const actionsSlot = "item.actions";
onMounted(() => {
  search();
  if (!props.picker) void loadPersonTypes();
});
</script>

<template>
  <div v-no-autofill class="generated-person-search">
    <form @submit.prevent="search">
      <v-row
        v-for="group in coreControls"
        :key="group.key"
        dense
        :align="props.picker ? 'center' : undefined"
      >
        <v-col
          v-for="field in group.fields"
          :key="field.key"
          cols="12"
          sm="6"
          :md="props.picker ? 3 : 12 / group.fields.length"
        >
          <GeneratedFieldControl
            :meta="field.meta"
            :label="field.label"
            :model-value="readField(draft, field.key)"
            density="compact"
            @update:model-value="writeField(draft, field.key, $event)"
          />
        </v-col>
      </v-row>
      <div
        class="person-search-actions"
        :class="props.picker ? 'mb-2' : 'mt-4 mb-4'"
      >
        <v-expansion-panels v-if="!props.picker" class="person-search-filters">
          <v-expansion-panel title="Additional filters">
            <v-expansion-panel-text>
              <v-row dense>
                <v-col
                  v-for="field in additionalControls"
                  :key="field.key"
                  cols="12"
                  sm="6"
                  lg="4"
                >
                  <GeneratedFieldControl
                    :meta="field.meta"
                    :label="field.label"
                    density="compact"
                    :model-value="readField(draft, field.key)"
                    @update:model-value="writeField(draft, field.key, $event)"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
        <div class="d-flex ga-2">
          <v-btn
            type="submit"
            color="primary"
            :loading="loading"
            :size="props.picker ? 'small' : 'large'"
            >Search</v-btn
          >
          <v-btn
            type="button"
            :size="props.picker ? 'small' : 'large'"
            @click="clear"
            >Clear</v-btn
          >
        </div>
      </div>
    </form>
    <v-alert v-if="error" type="error" role="alert" class="mb-4">
      {{ error }} Results could not be refreshed.
    </v-alert>
    <v-alert v-if="lookupError" type="error" role="alert" class="mb-4">{{
      lookupError
    }}</v-alert>
    <v-data-table-virtual
      v-if="props.picker"
      :headers="headers"
      :items="displayItems"
      :loading="loading"
      item-value="personId"
      density="compact"
      height="350"
    >
      <template v-slot:[nameSlot]="{ item }">
        <v-btn variant="text" @click="emit('personSelected', item.source)">{{
          item.name
        }}</v-btn>
      </template>

      <template v-slot:[dobSlot]="{ item }">{{
        item.dob?.toLocaleDateString() ?? ""
      }}</template>
    </v-data-table-virtual>
    <v-data-table-server
      v-else
      :headers="headers"
      :items="displayItems"
      :items-length="totalCount"
      :loading="loading"
      :page="page"
      :sort-by="sortBy"
      :items-per-page="perPage"
      :items-per-page-options="[10, 25, 50, 100]"
      item-value="personId"
      @update:page="changePage"
      @update:sort-by="changeSort"
      @update:items-per-page="resize"
    >
      <template
        v-for="header in customCells"
        :key="header.key"
        #[`item.${header.key}`]="{ item, value }"
      >
        <component :is="header.renderer" :item="item" :value="value" />
      </template>
      <template v-slot:[dobSlot]="{ item }">{{
        item.dob?.toLocaleDateString() ?? ""
      }}</template>
      <template v-slot:[updatedSlot]="{ item }">{{
        item.updatedOn?.toLocaleString() ?? ""
      }}</template>
      <template v-slot:[actionsSlot]="{ item }">
        <PersonPreviewDialog :person-dashboard="item" />
      </template>
    </v-data-table-server>
    <v-btn
      v-if="props.picker && canLoadMore"
      block
      :disabled="loading"
      @click="loadMore"
      >Load More...</v-btn
    >
  </div>
</template>

<style scoped>
.person-search-actions {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
}
.person-search-filters {
  flex: 1 1 240px;
  width: auto;
}
</style>
